import React from 'react'
import Layout from '../Layout'
import ResourceForm from '../../../Components/ResourceForm'
import FormFieldController from '../../../Components/FormFieldController'
import MapLocationPicker from '../../../Components/MapLocationPicker'
import BackLink from '../../../Components/BackLink'
import { positionSchema } from '../../../schemas'

export default function New({ account, accounts, navigation, position_types, position_categories, holes }) {
  const categoryOptions = position_categories.map(([label, value]) => ({
    label,
    value
  }))

  const holeOptions = [
    { label: 'Not associated with a hole', value: '' },
    ...holes
  ]

  // Filter position types based on category
  const getPositionTypeOptions = (category) => {
    const geoTypes = ['tee_box', 'green', 'halfway_house', 'clubhouse', 'pro_shop', 'bar', 'restaurant']
    const indoorTypes = ['dining_table', 'booth', 'bar_seat', 'terrace', 'private_room']

    const allowedTypes = category === 'IndoorPosition' ? indoorTypes : geoTypes
    return position_types
      .filter(([_, value]) => allowedTypes.includes(value))
      .map(([label, value]) => ({ label, value }))
  }

  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title="New Location"
    >
      <BackLink href="/manage/positions">Back to Locations</BackLink>

      <div className="bg-white shadow rounded-lg">
        <ResourceForm
          schema={positionSchema}
          resource="position"
          endpoint="/manage/positions"
          backLink="/manage/positions"
          submitLabel="Create Location"
          defaultValues={{
            name: '',
            category: 'GeoPosition',
            position_type: '',
            hole_id: '',
            lat: '',
            lng: '',
            walk_time_minutes: '',
            order_promo_text: '',
            table_number: '',
            capacity: '',
            area: ''
          }}
        >
          {({ control, errors, watch, setValue }) => {
            const category = watch('category')
            const isIndoor = category === 'IndoorPosition'
            const lat = watch('lat')
            const lng = watch('lng')

            return (
              <>
                <FormFieldController
                  control={control}
                  name="category"
                  label="Category"
                  type="select"
                  options={categoryOptions}
                  required
                  hint="Select Outdoor for golf course locations or Indoor for tables/seating areas"
                  errors={errors}
                />

                <FormFieldController
                  control={control}
                  name="name"
                  label="Name"
                  required
                  placeholder={isIndoor ? "e.g., Table 5, VIP Booth" : "e.g., Hole 1 Tee Box, Halfway House"}
                  errors={errors}
                />

                <FormFieldController
                  control={control}
                  name="position_type"
                  label="Type"
                  type="select"
                  options={getPositionTypeOptions(category)}
                  required
                  errors={errors}
                />

                {/* GeoPosition-specific fields */}
                {!isIndoor && (
                  <>
                    <FormFieldController
                      control={control}
                      name="hole_id"
                      label="Associated Hole"
                      type="select"
                      options={holeOptions}
                      hint="Optional - link this location to a specific hole"
                      errors={errors}
                    />

                    <MapLocationPicker
                      lat={lat}
                      lng={lng}
                      onChange={({ lat, lng }) => { setValue('lat', lat); setValue('lng', lng) }}
                      label="Location"
                      hint="Pan and zoom the map to set coordinates"
                      error={errors.lat?.message || errors.lng?.message}
                    />

                    <FormFieldController
                      control={control}
                      name="walk_time_minutes"
                      label="Walk Time (minutes)"
                      type="number"
                      hint="Minutes to walk from this location to catering - used to preset pickup time"
                      errors={errors}
                    />
                  </>
                )}

                {/* IndoorPosition-specific fields */}
                {isIndoor && (
                  <>
                    <FormFieldController
                      control={control}
                      name="table_number"
                      label="Table Number"
                      required
                      placeholder="e.g., T1, A5, Booth 3"
                      hint="Unique identifier for this table/seating area"
                      errors={errors}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormFieldController
                        control={control}
                        name="capacity"
                        label="Capacity"
                        type="number"
                        placeholder="e.g., 4"
                        hint="Maximum number of guests"
                        errors={errors}
                      />
                      <FormFieldController
                        control={control}
                        name="area"
                        label="Area/Section"
                        placeholder="e.g., Terrace, Main Floor"
                        hint="Location within the venue"
                        errors={errors}
                      />
                    </div>
                  </>
                )}

                <FormFieldController
                  control={control}
                  name="order_promo_text"
                  label="Order Promo Text"
                  type="textarea"
                  rows={2}
                  hint="Optional message shown to customers ordering from this location"
                  placeholder={isIndoor
                    ? "e.g., Welcome to your table! Order directly from your seat."
                    : "e.g., On the 16th hole? Perfect time to order!"
                  }
                  errors={errors}
                />
              </>
            )
          }}
        </ResourceForm>
      </div>
    </Layout>
  )
}
